// src/registered/site-admin/knowledge_base/KnowledgeBaseController.js
//
// Group-ESG-persona management of the global Knowledge Base corpus. Owns all
// state and returns a flat props object for the view — same controller shape as
// report_component_library.
//
// Cloned from precsion-med-react channels/compliance/controllers/useKnowledgeBaseController.js
// (react-query -> plain state + the SUSTINA dbPayload service).

import { useCallback, useEffect, useMemo, useState } from "react";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import KnowledgeDocumentDTO, {
  DOCUMENT_TYPES,
  chunkText,
  deriveNameFromUrl,
} from "./KnowledgeBaseDTO";
import { validateKnowledgeDocument } from "./KnowledgeBaseValidator";
import {
  fetchDocumentTypes,
  fetchDocumentStatuses,
  fetchKnowledgeDocuments,
  getKnowledgeDocument,
  addKnowledgeDocument,
  removeKnowledgeDocument,
  extractKnowledgeSource,
  stageKnowledgeFile,
} from "./knowledgeBaseInteractionService";
import { MAX_SOURCE_BYTES } from "./KnowledgeBaseDTO";

const EMPTY_FORM = {
  name: "",
  documentTypeCode: DOCUMENT_TYPES[0].code,
  categoryTag: "",
  effectiveDate: "",
  body: "",
  sourceKind: "text", // "text" paste | "file" upload | "url" fetch
  sourceUrl: "",
};

export default function KnowledgeBaseController(siteKey) {
  const config = useMemo(() => ({ siteKey }), [siteKey]);

  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState(DOCUMENT_TYPES);
  const [statuses, setStatuses] = useState([]);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [form, setForm] = useState(EMPTY_FORM);
  const [pickedFile, setPickedFile] = useState(null); // File, for sourceKind === "file"
  const [errors, setErrors] = useState({});
  const [categoryFilter, setCategoryFilter] = useState("");

  const [openDoc, setOpenDoc] = useState(null); // full doc + chunks, for the inspector panel
  const [openLoading, setOpenLoading] = useState(false);

  // --- load ---------------------------------------------------------------

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const rows = await fetchKnowledgeDocuments(
        { categoryTag: categoryFilter || null },
        config,
      );
      setDocuments(rows || []);
    } catch (err) {
      setError(err?.message || "Could not load the knowledge base.");
    } finally {
      setLoading(false);
    }
  }, [config, categoryFilter]);

  useEffect(() => {
    let alive = true;
    (async () => {
      const [types, sts] = await Promise.all([
        fetchDocumentTypes(config),
        fetchDocumentStatuses(config),
      ]);
      if (!alive) return;
      if (types.length) {
        setDocumentTypes(
          types.map((t) => ({
            code: GoldenSourceHelper.get(t, "DocumentTypeCode"),
            label: GoldenSourceHelper.get(t, "Label"),
          })),
        );
      }
      setStatuses(sts || []);
    })();
    return () => {
      alive = false;
    };
  }, [config]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // --- derived ----------------------------------------------------------

  const categories = useMemo(
    () =>
      [
        ...new Set(
          documents.map((d) => GoldenSourceHelper.get(d, "CategoryTag")).filter(Boolean),
        ),
      ].sort(),
    [documents],
  );

  const rows = useMemo(
    () =>
      documents.map((d) => ({
        id: GoldenSourceHelper.get(d, "KnowledgeDocumentId"),
        name: GoldenSourceHelper.get(d, "Name"),
        typeCode: GoldenSourceHelper.get(d, "DocumentTypeCode"),
        typeLabel:
          GoldenSourceHelper.get(d, "DocumentTypeLabel") ||
          GoldenSourceHelper.get(d, "DocumentTypeCode"),
        categoryTag: GoldenSourceHelper.get(d, "CategoryTag") || "",
        effectiveDate: (GoldenSourceHelper.get(d, "EffectiveDate") || "").slice(0, 10),
        statusCode: GoldenSourceHelper.get(d, "StatusCode") || "",
        statusLabel:
          GoldenSourceHelper.get(d, "StatusLabel") || GoldenSourceHelper.get(d, "StatusCode") || "",
        sourceKind: GoldenSourceHelper.get(d, "SourceKind") || "text",
        chunkCount: GoldenSourceHelper.get(d, "ChunkCount") || 0,
      })),
    [documents],
  );

  const totals = useMemo(
    () => ({
      documents: rows.length,
      passages: rows.reduce((n, r) => n + (r.chunkCount || 0), 0),
    }),
    [rows],
  );

  // --- form -----------------------------------------------------------------

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setFile = (file) => {
    setPickedFile(file || null);
    setErrors((e) => ({ ...e, file: undefined }));
  };
  const resetForm = () => {
    setForm(EMPTY_FORM);
    setPickedFile(null);
    setErrors({});
  };

  const generalError =
    errors._form ||
    errors.fetch ||
    (form.sourceKind === "url" ? errors.body : null);

  const submit = async () => {
    const isUrl = form.sourceKind === "url";
    const isFile = form.sourceKind === "file";
    const typeCodes = documentTypes.map((t) => t.code);
    const { errors: e } = validateKnowledgeDocument(
      form,
      rows.map((r) => ({ knowledgeDocumentId: r.id, name: r.name })),
      typeCodes,
    );
    if (isFile && !pickedFile) e.file = "Choose a file to upload.";
    if (isFile && pickedFile && pickedFile.size > MAX_SOURCE_BYTES) {
      e.file = `File is larger than ${Math.round(MAX_SOURCE_BYTES / (1024 * 1024))} MB.`;
    }
    // body is only required for the paste path
    if (isFile) delete e.body;

    setErrors(e);
    if (Object.keys(e).length > 0) return false;

    setSubmitting(true);
    setError("");
    setNotice("");
    try {
      let bodyText = form.body;
      let name = form.name.trim();
      const provenance = {
        SourceKind: form.sourceKind,
        SourceUrl: isUrl ? form.sourceUrl.trim() : null,
      };

      if (isUrl || isFile) {
        let source;
        if (isUrl) {
          source = { sourceKind: "url", sourceUrl: form.sourceUrl.trim() };
        } else {
          const staged = await stageKnowledgeFile(pickedFile, config);
          source = {
            sourceKind: "file",
            bucket: staged.bucket,
            key: staged.key,
            originalFileName: pickedFile.name,
            mimeHint: pickedFile.type || pickedFile.name.split(".").pop(),
          };
          provenance.OriginalFileName = pickedFile.name;
        }

        const extracted = await extractKnowledgeSource(source, config);
        if (!extracted.text || !extracted.text.trim()) {
          setErrors({ [isUrl ? "sourceUrl" : "file"]: "No readable text could be extracted from that source." });
          return false;
        }
        bodyText = isUrl
          ? `Source: ${form.sourceUrl.trim()}\n\n${extracted.text}`
          : extracted.text;
        if (!name) {
          name =
            extracted.title?.trim() ||
            (isUrl ? deriveNameFromUrl(form.sourceUrl.trim()) : pickedFile.name.replace(/\.[a-z0-9]+$/i, ""));
        }
        provenance.MimeType = extracted.mimeType || null;
        provenance.SourceFormat = extracted.format || null;
        provenance.ContentSha256 = extracted.sha256 || null;
        provenance.ParserName = extracted.parserName || null;
        provenance.ParserVersion = extracted.parserVersion || null;
      }

      if (!name) {
        setErrors({ name: "Document name is required." });
        return false;
      }

      const chunks = chunkText(bodyText);
      if (chunks.length === 0) {
        setErrors({ [isFile ? "file" : "body"]: "The document has no usable text to index." });
        return false;
      }

      await addKnowledgeDocument(
        {
          Name: name,
          DocumentTypeCode: form.documentTypeCode,
          CategoryTag: form.categoryTag.trim() || null,
          EffectiveDate: form.effectiveDate || null,
          RawText: bodyText,
          StatusCode: "INDEXED",
          Chunks: chunks,
          ...provenance,
        },
        config,
      );

      resetForm();
      await refresh();
      setNotice(`Registered "${name}" — ${chunks.length} passage${chunks.length === 1 ? "" : "s"} indexed.`);
      return true;
    } catch (err) {
      if (err?.fieldErrors) setErrors(err.fieldErrors);
      else setErrors({ _form: err?.message || "Could not register the document." });
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  // --- row actions ------------------------------------------------------

  const inspect = async (id) => {
    if (openDoc && openDoc.knowledgeDocumentId === id) {
      setOpenDoc(null);
      return;
    }
    setOpenLoading(true);
    setError("");
    try {
      const full = await getKnowledgeDocument(id, config);
      setOpenDoc(full ? new KnowledgeDocumentDTO({
        knowledgeDocumentId: GoldenSourceHelper.get(full, "KnowledgeDocumentId") || id,
        name: GoldenSourceHelper.get(full, "Name"),
        documentTypeCode: GoldenSourceHelper.get(full, "DocumentTypeCode"),
        documentTypeLabel: GoldenSourceHelper.get(full, "DocumentTypeLabel"),
        categoryTag: GoldenSourceHelper.get(full, "CategoryTag"),
        effectiveDate: GoldenSourceHelper.get(full, "EffectiveDate"),
        statusCode: GoldenSourceHelper.get(full, "StatusCode"),
        statusLabel: GoldenSourceHelper.get(full, "StatusLabel"),
        sourceKind: GoldenSourceHelper.get(full, "SourceKind"),
        sourceUrl: GoldenSourceHelper.get(full, "SourceUrl"),
        rawText: GoldenSourceHelper.get(full, "RawText"),
        chunkCount: GoldenSourceHelper.get(full, "ChunkCount"),
        chunks: (full.chunks || []).map((c) => ({
          chunkId: GoldenSourceHelper.get(c, "ChunkId"),
          chunkIndex: GoldenSourceHelper.get(c, "ChunkIndex"),
          bodyText: GoldenSourceHelper.get(c, "BodyText"),
          tokenCount: GoldenSourceHelper.get(c, "TokenCount"),
          section: GoldenSourceHelper.get(c, "Section"),
        })),
      }) : null);
      if (!full) setError(`Could not open document ${id}.`);
    } finally {
      setOpenLoading(false);
    }
  };

  const removeDocument = async (id) => {
    setError("");
    try {
      await removeKnowledgeDocument(id, config);
      if (openDoc && openDoc.knowledgeDocumentId === id) setOpenDoc(null);
      await refresh();
      setNotice("Document removed.");
    } catch (err) {
      setError(err?.message || "Could not remove the document.");
    }
  };

  // live passage-count preview for the paste-text form
  const pendingChunkCount = useMemo(
    () => (form.sourceKind === "text" ? chunkText(form.body).length : 0),
    [form.sourceKind, form.body],
  );

  return {
    // data
    rows,
    documentTypes,
    statuses,
    categories,
    totals,
    loading,
    // form
    form,
    pickedFile,
    errors,
    generalError,
    submitting,
    pendingChunkCount,
    setField,
    setFile,
    submit,
    resetForm,
    // filter
    categoryFilter,
    setCategoryFilter,
    // row actions
    inspect,
    removeDocument,
    openDoc,
    openLoading,
    // page-level messages
    error,
    notice,
    refresh,
  };
}
